import React from "react";
import Layout from "../components/Layout";
import SEOHead from "../components/SEOHead";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { getImage } from "../brand/images";
import { Download, Play, FileText } from "lucide-react";
import { Link } from "react-router-dom";

const STORYBOARD = [
  { id: 1, time: "0:00 – 0:10", title: "Cold open — control room",
    visual: "Slow dolly across a dark navy operations floor. Gold accent lines sweep across monitors. Electric-blue pulse-dots highlight live devices.",
    voiceover: "In every city, every county, every data center — public infrastructure depends on decisions that rarely see daylight. AuditIQ changes that.",
    image: getImage("cyber-soc-defense-center") },
  { id: 2, time: "0:10 – 0:25", title: "Public-sector accountability",
    visual: "Map overlay pans across North American counties. Gold dots highlight pilot entities; cards materialize showing budget-risk scores.",
    voiceover: "AuditIQ is the GovTech platform for public-sector accountability — AI-powered budget transparency, infrastructure risk scoring, and defensive cybersecurity — in one executive dashboard.",
    image: getImage("gov-capitol-building") },
  { id: 3, time: "0:25 – 0:45", title: "Budget scanner demo",
    visual: "UI zoom into /budget-scanner. Department bars animate. Claude narrative text types itself.",
    voiceover: "Our crawler discovers publicly-available adopted budgets, capital plans, and procurement summaries — then our AI produces a non-accusatory executive summary in seconds. Variances become review-worthy indicators, not headlines.",
    image: getImage("dash-executive-analytics") },
  { id: 4, time: "0:45 – 1:05", title: "Infrastructure & reallocation",
    visual: "Capital projects list. Drainage project highlighted with +53% escalation. Card flips to show community benefit.",
    voiceover: "AuditIQ recommends infrastructure reallocation framed around community benefit — drainage, water, public safety, parks, and resilience. Residents get questions worth asking. Officials get decision-support.",
    image: getImage("infra-stormwater-drainage") },
  { id: 5, time: "1:05 – 1:25", title: "CyberWater Security Layer",
    visual: "Split-screen: live AWG-03 unit on left, command-validation audit trail on right. Pump shutdown command requires dual approval.",
    voiceover: "CyberWater protects atmospheric water generators, data-center cooling loops, PLCs, pumps, and valves with zero-trust device identity and multi-party command authorization. Defensive-only. Forensic-ready.",
    image: getImage("water-data-center-cooling") },
  { id: 6, time: "1:25 – 1:40", title: "Data center water production",
    visual: "Six AWG tiles animate with live liters-per-hour counters. Facility total rolls to 60 000 L/day.",
    voiceover: "Each data center facility runs six atmospheric water generation units, each targeting 10 000 liters per day — 60 000 liters of on-site water, continuously monitored and cyber-protected.",
    image: getImage("water-industrial-pumps") },
  { id: 7, time: "1:40 – 1:55", title: "Certification & pilot program",
    visual: "Certification page cards fan out. No-cost pathway highlighted in electric blue.",
    voiceover: "Municipal staff, transparency officers, and infrastructure operators can become AuditIQ-certified — with no-cost pathways available to eligible agencies, students, and pilot partners.",
    image: getImage("gov-council-chamber") },
  { id: 8, time: "1:55 – 2:05", title: "Close — brand bumper",
    visual: "AuditIQ shield animates on navy. Gold rule draws across. Closing card: AUDITIQ — accountability, engineered.",
    voiceover: "AuditIQ. Accountability, engineered. Visit auditiq.gov to launch the interactive demo.",
    image: getImage("dash-transparency-report") },
];

const FULL_SCRIPT = STORYBOARD.map(s =>
  `SCENE ${s.id} · ${s.time} · ${s.title}\nVISUAL: ${s.visual}\nVOICEOVER: ${s.voiceover}\n`
).join("\n");

const VideoDemo = () => {
  const downloadScript = () => {
    const blob = new Blob(
      [`AuditIQ — Product Demo Video Script\nRuntime: ~2:05\n\n${FULL_SCRIPT}`],
      { type: "text/plain;charset=utf-8" }
    );
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "auditiq-video-demo-script.txt";
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <Layout>
      <SEOHead title="Product Demo Video & Storyboard | AuditIQ" path="/video-demo"
        description="AuditIQ product demo video storyboard, shot list, and voice-over script — ready for external production." />
      <PageHeader eyebrow="Video Demo" demo
        title="Product Demo · Storyboard & Script"
        subtitle="Production-ready storyboard for the AuditIQ 2-minute product demo video. Shot-by-shot visuals, narration, and a one-click script download."
        actions={
          <div className="flex gap-2">
            <Button className="btn-gold h-11 px-5" onClick={downloadScript} data-testid="download-script">
              <Download size={14} className="mr-2" /> Download Script (.txt)
            </Button>
            <Link to="/demo"><Button className="btn-ghost-gold h-11 px-5" data-testid="to-interactive-demo">
              <Play size={14} className="mr-2" /> Open Interactive Demo
            </Button></Link>
          </div>
        }
      />

      {/* Hero video placeholder */}
      <section className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="glass-accent p-0 overflow-hidden relative aspect-video flex items-center justify-center">
          <img src={getImage("cyber-soc-defense-center").src} alt="AuditIQ video demo cover" className="absolute inset-0 w-full h-full object-cover opacity-30" />
          <div className="relative text-center">
            <div className="w-20 h-20 rounded-full border-2 border-electric flex items-center justify-center mx-auto">
              <Play size={36} className="text-electric ml-1" />
            </div>
            <div className="mt-5 text-[11px] uppercase tracking-[0.28em] text-electric">Video Production Ready</div>
            <h3 className="font-display text-3xl mt-2">AuditIQ — Product Demo · 2:05</h3>
            <p className="text-sm text-cream-dim mt-2 max-w-xl mx-auto">
              Awaiting external video production. Use the storyboard below plus the downloadable script to shoot or render the final cut.
            </p>
          </div>
        </div>
      </section>

      {/* Storyboard */}
      <section className="max-w-[1400px] mx-auto px-6 pb-10">
        <div className="text-[11px] uppercase tracking-[0.28em] text-electric mb-3">Storyboard</div>
        <h3 className="font-display text-3xl">Shot-by-shot</h3>
        <div className="data-line mt-3 mb-8" />
        <div className="grid md:grid-cols-2 gap-5">
          {STORYBOARD.map(s => (
            <div key={s.id} className="glass p-0 overflow-hidden" data-testid={`scene-${s.id}`}>
              <img src={s.image.src} alt={s.image.alt} className="w-full aspect-[16/9] object-cover opacity-80" />
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-electric">Scene {s.id} · {s.time}</div>
                  <span className="text-[10px] uppercase text-gold tracking-wider">{s.title}</span>
                </div>
                <div className="text-xs text-steel uppercase tracking-wider mt-3">Visual</div>
                <div className="text-sm text-cream-dim mt-1">{s.visual}</div>
                <div className="text-xs text-steel uppercase tracking-wider mt-3">Voiceover</div>
                <div className="text-sm text-cream mt-1 italic">"{s.voiceover}"</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Narration script */}
      <section className="max-w-[1400px] mx-auto px-6 pb-20">
        <div className="glass p-8">
          <div className="flex items-center gap-3 mb-3">
            <FileText size={18} className="text-electric" />
            <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Narration Script</div>
          </div>
          <pre className="whitespace-pre-wrap text-sm text-cream-dim font-sans leading-relaxed" data-testid="narration-script">
{FULL_SCRIPT}
          </pre>
        </div>
      </section>
    </Layout>
  );
};

export default VideoDemo;
